/**
 * @file Skeleton.tsx
 * @brief Pulsing placeholder block for loading states.
 *
 * @details
 * One tone everywhere (01-10-2026): the phone's skeleton grey
 * (quaternaryDark), which reads on both the page and a white card. The old
 * `bg-muted` (#F3F3F1) sat almost on the page colour, so admin skeletons
 * were nearly invisible.
 * Pages stack several bones inside one `animate-pulse` wrapper so they
 * pulse together: pass `still` to the bones to skip their own pulse.
 *
 * @author DES
 * @date 26-05-2026
 */

import type { HTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Inside a pulsing wrapper: no pulse of its own. */
  still?: boolean;
}

export function Skeleton({ className, still = false, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "rounded-md bg-app-quaternary-dark",
        !still && "animate-pulse",
        className,
      )}
      {...props}
    />
  );
}

/**
 * List-row placeholder for the admin cards (analytics tables, lists):
 * hairline rows with a name bar (and an optional second line) on the left
 * and a number bar on the right, pulsing together.
 */
export function SkeletonRows({
  rows = 4,
  twoLine = false,
  className,
}: {
  rows?: number;
  twoLine?: boolean;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn("animate-pulse divide-y divide-border/60 border-t border-border/60", className)}
    >
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center justify-between gap-4 px-4 py-3 md:px-5">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton still className={cn("h-3.5", i % 2 ? "w-2/5" : "w-1/2")} />
            {twoLine ? <Skeleton still className="h-3 w-1/3" /> : null}
          </div>
          <Skeleton still className="h-3.5 w-12" />
        </div>
      ))}
    </div>
  );
}

/**
 * Form / settings placeholder for the admin cards: label + field pairs,
 * pulsing together. For a section that loads a form or a panel of settings.
 */
export function SkeletonFields({
  fields = 3,
  className,
}: {
  fields?: number;
  className?: string;
}) {
  return (
    <div role="status" aria-label="Loading" className={cn("animate-pulse space-y-5", className)}>
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton still className="h-3 w-24" />
          <Skeleton still className={cn("h-9", i % 2 ? "w-2/3" : "w-full")} />
        </div>
      ))}
    </div>
  );
}
